import gql from 'graphql-tag';
import * as Apollo from '@apollo/client';
import * as Types from '../../../../server/graphql/__generated.types';

import { SectionListFragmentDoc, SectionFragmentDoc } from '../../fragments.generated';

const defaultOptions = {} as const;
export type SectionsQueryVariables = Types.Exact<{
  inputs: Types.GetSectionsInputs;
}>;

export type SectionsQuery = {
  __typename?: 'Query';
  sections: {
    __typename?: 'SectionsConnection';
    edges: Array<{
      __typename?: 'SectionEdge';
      cursor: string;
      node: {
        __typename?: 'Section';
        name: string;
        sequence: string;
        projectId: string;
        defaultAvatarId?: number | null;
        defaultAvatarVariantId?: number | null;
        defaultTtsVersion?: string | null;
        createdAt: string;
        updatedAt: string;
        id: string;
        commentCount?: number | null;
        unviewedCommentCount?: number | null;
        pauseAfter?: number | null;
        editor?: string | null;
        currentTake?: {
          __typename?: 'Take';
          id: string;
          name: string;
          audioFormat: string;
          isCurrentTake: boolean;
          userId: string;
          gcsFileName: string;
          text: string;
          formattedText: string;
          editorSchemaVersion: number;
          ttsVersion: string;
          avatarId: number;
          avatarVariantId: number;
          createdAt: string;
          updatedAt: string;
          duration?: number | null;
          sampleRate?: number | null;
          textLength: number;
          hasCurrentUserFeedback?: boolean | null;
        } | null;
      };
    }>;
    pageInfo: {
      __typename?: 'PageInfo';
      endCursor?: string | null;
      hasNextPage: boolean;
    };
  };
};

export type SectionQueryVariables = Types.Exact<{
  id: Types.Scalars['ID']['input'];
}>;

export type SectionQuery = {
  __typename?: 'Query';
  section?: {
    __typename?: 'Section';
    name: string;
    sequence: string;
    projectId: string;
    defaultAvatarId?: number | null;
    defaultAvatarVariantId?: number | null;
    defaultTtsVersion?: string | null;
    createdAt: string;
    updatedAt: string;
    id: string;
    commentCount?: number | null;
    unviewedCommentCount?: number | null;
    pauseAfter?: number | null;
    editor?: string | null;
    currentTake?: {
      __typename?: 'Take';
      id: string;
      name: string;
      audioFormat: string;
      isCurrentTake: boolean;
      userId: string;
      gcsFileName: string;
      text: string;
      formattedText: string;
      editorSchemaVersion: number;
      ttsVersion: string;
      avatarId: number;
      avatarVariantId: number;
      createdAt: string;
      updatedAt: string;
      duration?: number | null;
      sampleRate?: number | null;
      textLength: number;
      hasCurrentUserFeedback?: boolean | null;
    } | null;
  } | null;
};

export type CreateSectionMutationVariables = Types.Exact<{
  inputs: Types.CreateSectionInputs;
}>;

export type CreateSectionMutation = {
  __typename?: 'Mutation';
  createSection: {
    __typename?: 'CreateSectionMutationResponse';
    code: string;
    success: boolean;
    message: string;
    section?: {
      __typename?: 'Section';
      id: string;
      name: string;
      sequence: string;
      projectId: string;
      defaultAvatarId?: number | null;
      defaultAvatarVariantId?: number | null;
      defaultTtsVersion?: string | null;
      createdAt: string;
      updatedAt: string;
      editor?: string | null;
    } | null;
  };
};

export type BulkCreateSectionsMutationVariables = Types.Exact<{
  inputs: Types.BulkCreateSectionsInputs;
}>;

export type BulkCreateSectionsMutation = {
  __typename?: 'Mutation';
  bulkCreateSections: {
    __typename?: 'BulkCreateSectionsMutationResponse';
    code: string;
    success: boolean;
    message: string;
    sections?: Array<{
      __typename?: 'Section';
      id: string;
      name: string;
      sequence: string;
      projectId: string;
      createdAt: string;
      updatedAt: string;
      defaultAvatarId?: number | null;
      defaultAvatarVariantId?: number | null;
      defaultTtsVersion?: string | null;
      editor?: string | null;
    }> | null;
  };
};

export type DeleteSectionMutationVariables = Types.Exact<{
  inputs: Types.DeleteSectionInputs;
}>;

export type DeleteSectionMutation = {
  __typename?: 'Mutation';
  deleteSection: {
    __typename?: 'DeleteSectionMutationResponse';
    code: string;
    success: boolean;
    message: string;
    affectedRows?: number | null;
  };
};

export type BulkDeleteSectionsMutationVariables = Types.Exact<{
  inputs: Types.BulkDeleteSectionsInputs;
}>;

export type BulkDeleteSectionsMutation = {
  __typename?: 'Mutation';
  bulkDeleteSections: {
    __typename?: 'BulkDeleteSectionsMutationResponse';
    code: string;
    success: boolean;
    message: string;
    affectedRows?: number | null;
  };
};

export type UpdateSectionNameMutationVariables = Types.Exact<{
  inputs: Types.UpdateSectionNameInputs;
}>;

export type UpdateSectionNameMutation = {
  __typename?: 'Mutation';
  updateSectionName: {
    __typename?: 'UpdateSectionNameMutationResponse';
    code: string;
    success: boolean;
    message: string;
    section?: {
      __typename?: 'Section';
      id: string;
      name: string;
      updatedAt: string;
    } | null;
  };
};

export type UpdateSectionEditorMutationVariables = Types.Exact<{
  inputs: Types.UpdateSectionInputs;
}>;

export type UpdateSectionEditorMutation = {
  __typename?: 'Mutation';
  updateSection: {
    __typename?: 'UpdateSectionMutationResponse';
    code: string;
    success: boolean;
    message: string;
    section?: {
      __typename?: 'Section';
      id: string;
      editor?: string | null;
      updatedAt: string;
    } | null;
  };
};

export type BulkUpdateSectionEditorsMutationVariables = Types.Exact<{
  inputs: Types.BulkUpdateSectionsInputs;
}>;

export type BulkUpdateSectionEditorsMutation = {
  __typename?: 'Mutation';
  bulkUpdateSections: {
    __typename?: 'BulkUpdateSectionsMutationResponse';
    code: string;
    success: boolean;
    message: string;
    sections?: Array<{
      __typename?: 'Section';
      id: string;
      editor?: string | null;
      updatedAt: string;
    }> | null;
  };
};

export type StartBulkGenerateTakeRunMutationVariables = Types.Exact<{
  inputs: Types.StartBulkGenerateTakeRunInputs;
}>;

export type StartBulkGenerateTakeRunMutation = {
  __typename?: 'Mutation';
  startBulkGenerateTakeRun: {
    __typename?: 'StartBulkGenerateTakeRunMutationResponse';
    code: string;
    success: boolean;
    message: string;
    runId?: string | null;
  };
};

export type ActiveBulkGenerateTakeRunQueryVariables = Types.Exact<{
  inputs: Types.ActiveBulkGenerateTakeRunInputs;
}>;

export type ActiveBulkGenerateTakeRunQuery = {
  __typename?: 'Query';
  activeBulkGenerateTakeRun?: {
    __typename?: 'ActiveBulkGenerateTakeRun';
    runId: string;
    pendingItems: Array<{
      __typename?: 'ActiveBulkGenerateTakeRunItem';
      sectionId: string;
      text: string;
      formattedText: string;
      avatarId: number;
      avatarVariantId: number;
      ttsVersion: string;
    }>;
  } | null;
};

export type CancelBulkGenerateTakeRunMutationVariables = Types.Exact<{
  inputs: Types.CancelBulkGenerateTakeRunInputs;
}>;

export type CancelBulkGenerateTakeRunMutation = {
  __typename?: 'Mutation';
  cancelBulkGenerateTakeRun: {
    __typename?: 'CancelBulkGenerateTakeRunMutationResponse';
    code: string;
    success: boolean;
    message: string;
    canceled: boolean;
  };
};

export type BulkRunItemChangedSubscriptionVariables = Types.Exact<{
  inputs: Types.BulkRunItemChangedSubscriptionInputs;
}>;

export type BulkRunItemChangedSubscription = {
  __typename?: 'Subscription';
  bulkRunItemChanged: {
    __typename?: 'BulkRunItemChangedEvent';
    projectId: string;
    runId: string;
    bulkRunItemId: string;
    sectionId: string;
    status: Types.BulkRunItemChangedStatus;
    takeId?: string | null;
    errorCode?: string | null;
    errorMessage?: string | null;
  };
};

export type BulkRunStartedSubscriptionVariables = Types.Exact<{
  inputs: Types.BulkRunStartedSubscriptionInputs;
}>;

export type BulkRunStartedSubscription = {
  __typename?: 'Subscription';
  bulkRunStarted: {
    __typename?: 'BulkRunStartedEvent';
    projectId: string;
    runId: string;
  };
};

export type UpdateSectionVoiceMutationVariables = Types.Exact<{
  inputs: Types.UpdateSectionVoiceInputs;
}>;

export type UpdateSectionVoiceMutation = {
  __typename?: 'Mutation';
  updateSectionVoice: {
    __typename?: 'UpdateSectionVoiceMutationResponse';
    code: string;
    success: boolean;
    message: string;
    section?: {
      __typename?: 'Section';
      id: string;
      defaultAvatarId?: number | null;
      defaultAvatarVariantId?: number | null;
      defaultTtsVersion?: string | null;
      projectId: string;
    } | null;
    project?: {
      __typename?: 'Project';
      id: string;
      voices: Array<{ __typename?: 'ProjectVoices'; id: number }>;
    } | null;
  };
};

export type UpdateSectionVoicesMutationVariables = Types.Exact<{
  inputs: Types.UpdateSectionVoicesInputs;
}>;

export type UpdateSectionVoicesMutation = {
  __typename?: 'Mutation';
  updateSectionVoices: {
    __typename?: 'UpdateSectionVoicesMutationResponse';
    code: string;
    success: boolean;
    message: string;
    sections?: Array<{
      __typename?: 'Section';
      id: string;
      defaultAvatarId?: number | null;
      defaultAvatarVariantId?: number | null;
      defaultTtsVersion?: string | null;
      projectId: string;
    }> | null;
    projects?: Array<{
      __typename?: 'Project';
      id: string;
      voices: Array<{ __typename?: 'ProjectVoices'; id: number }>;
    }> | null;
  };
};

export type SectionsChangedSubscriptionVariables = Types.Exact<{
  inputs: Types.SectionsChangedSubscriptionInputs;
}>;

export type SectionsChangedSubscription = {
  __typename?: 'Subscription';
  sectionsChanged: {
    __typename?: 'SectionsChangedEvent';
    projectId: string;
    kind: Types.SectionChangeKind;
    sectionIds: Array<string>;
    sections: Array<{
      __typename?: 'Section';
      name: string;
      sequence: string;
      projectId: string;
      defaultAvatarId?: number | null;
      defaultAvatarVariantId?: number | null;
      defaultTtsVersion?: string | null;
      createdAt: string;
      updatedAt: string;
      id: string;
      commentCount?: number | null;
      unviewedCommentCount?: number | null;
      pauseAfter?: number | null;
      editor?: string | null;
      currentTake?: {
        __typename?: 'Take';
        id: string;
        name: string;
        audioFormat: string;
        isCurrentTake: boolean;
        userId: string;
        gcsFileName: string;
        text: string;
        formattedText: string;
        editorSchemaVersion: number;
        ttsVersion: string;
        avatarId: number;
        avatarVariantId: number;
        createdAt: string;
        updatedAt: string;
        duration?: number | null;
        sampleRate?: number | null;
        textLength: number;
        hasCurrentUserFeedback?: boolean | null;
      } | null;
    }>;
  };
};

export const SectionsDocument = gql`
  query Sections($inputs: GetSectionsInputs!) {
    sections(inputs: $inputs) {
      ...SectionListFragment
    }
  }
  ${SectionListFragmentDoc}
`;

/**
 * __useSectionsQuery__
 *
 * To run a query within a React component, call `useSectionsQuery` and pass it any options that fit your needs.
 * When your component renders, `useSectionsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useSectionsQuery({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useSectionsQuery(
  baseOptions: Apollo.QueryHookOptions<SectionsQuery, SectionsQueryVariables>
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<SectionsQuery, SectionsQueryVariables>(
    SectionsDocument,
    options
  );
}
export function useSectionsLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<SectionsQuery, SectionsQueryVariables>
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<SectionsQuery, SectionsQueryVariables>(
    SectionsDocument,
    options
  );
}
export type SectionsQueryHookResult = ReturnType<typeof useSectionsQuery>;
export type SectionsLazyQueryHookResult = ReturnType<typeof useSectionsLazyQuery>;
export type SectionsQueryResult = Apollo.QueryResult<
  SectionsQuery,
  SectionsQueryVariables
>;
export const SectionDocument = gql`
  query Section($id: ID!) {
    section(id: $id) {
      ...SectionFragment
    }
  }
  ${SectionFragmentDoc}
`;

/**
 * __useSectionQuery__
 *
 * To run a query within a React component, call `useSectionQuery` and pass it any options that fit your needs.
 * When your component renders, `useSectionQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useSectionQuery({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useSectionQuery(
  baseOptions: Apollo.QueryHookOptions<SectionQuery, SectionQueryVariables>
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<SectionQuery, SectionQueryVariables>(SectionDocument, options);
}
export function useSectionLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<SectionQuery, SectionQueryVariables>
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<SectionQuery, SectionQueryVariables>(
    SectionDocument,
    options
  );
}
export type SectionQueryHookResult = ReturnType<typeof useSectionQuery>;
export type SectionLazyQueryHookResult = ReturnType<typeof useSectionLazyQuery>;
export type SectionQueryResult = Apollo.QueryResult<SectionQuery, SectionQueryVariables>;
export const CreateSectionDocument = gql`
  mutation CreateSection($inputs: CreateSectionInputs!) {
    createSection(inputs: $inputs) {
      code
      success
      message
      section {
        id
        name
        sequence
        projectId
        defaultAvatarId
        defaultAvatarVariantId
        defaultTtsVersion
        createdAt
        updatedAt
        editor
      }
    }
  }
`;
export type CreateSectionMutationFn = Apollo.MutationFunction<
  CreateSectionMutation,
  CreateSectionMutationVariables
>;

/**
 * __useCreateSectionMutation__
 *
 * To run a mutation, you first call `useCreateSectionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCreateSectionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [createSectionMutation, { data, loading, error }] = useCreateSectionMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useCreateSectionMutation(
  baseOptions?: Apollo.MutationHookOptions<
    CreateSectionMutation,
    CreateSectionMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<CreateSectionMutation, CreateSectionMutationVariables>(
    CreateSectionDocument,
    options
  );
}
export type CreateSectionMutationHookResult = ReturnType<typeof useCreateSectionMutation>;
export type CreateSectionMutationResult = Apollo.MutationResult<CreateSectionMutation>;
export type CreateSectionMutationOptions = Apollo.BaseMutationOptions<
  CreateSectionMutation,
  CreateSectionMutationVariables
>;
export const BulkCreateSectionsDocument = gql`
  mutation BulkCreateSections($inputs: BulkCreateSectionsInputs!) {
    bulkCreateSections(inputs: $inputs) {
      code
      success
      message
      sections {
        id
        name
        sequence
        projectId
        createdAt
        updatedAt
        defaultAvatarId
        defaultAvatarVariantId
        defaultTtsVersion
        editor
      }
    }
  }
`;
export type BulkCreateSectionsMutationFn = Apollo.MutationFunction<
  BulkCreateSectionsMutation,
  BulkCreateSectionsMutationVariables
>;

/**
 * __useBulkCreateSectionsMutation__
 *
 * To run a mutation, you first call `useBulkCreateSectionsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useBulkCreateSectionsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [bulkCreateSectionsMutation, { data, loading, error }] = useBulkCreateSectionsMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useBulkCreateSectionsMutation(
  baseOptions?: Apollo.MutationHookOptions<
    BulkCreateSectionsMutation,
    BulkCreateSectionsMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    BulkCreateSectionsMutation,
    BulkCreateSectionsMutationVariables
  >(BulkCreateSectionsDocument, options);
}
export type BulkCreateSectionsMutationHookResult = ReturnType<
  typeof useBulkCreateSectionsMutation
>;
export type BulkCreateSectionsMutationResult =
  Apollo.MutationResult<BulkCreateSectionsMutation>;
export type BulkCreateSectionsMutationOptions = Apollo.BaseMutationOptions<
  BulkCreateSectionsMutation,
  BulkCreateSectionsMutationVariables
>;
export const DeleteSectionDocument = gql`
  mutation DeleteSection($inputs: DeleteSectionInputs!) {
    deleteSection(inputs: $inputs) {
      code
      success
      message
      affectedRows
    }
  }
`;
export type DeleteSectionMutationFn = Apollo.MutationFunction<
  DeleteSectionMutation,
  DeleteSectionMutationVariables
>;

/**
 * __useDeleteSectionMutation__
 *
 * To run a mutation, you first call `useDeleteSectionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeleteSectionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [deleteSectionMutation, { data, loading, error }] = useDeleteSectionMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useDeleteSectionMutation(
  baseOptions?: Apollo.MutationHookOptions<
    DeleteSectionMutation,
    DeleteSectionMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<DeleteSectionMutation, DeleteSectionMutationVariables>(
    DeleteSectionDocument,
    options
  );
}
export type DeleteSectionMutationHookResult = ReturnType<typeof useDeleteSectionMutation>;
export type DeleteSectionMutationResult = Apollo.MutationResult<DeleteSectionMutation>;
export type DeleteSectionMutationOptions = Apollo.BaseMutationOptions<
  DeleteSectionMutation,
  DeleteSectionMutationVariables
>;
export const BulkDeleteSectionsDocument = gql`
  mutation BulkDeleteSections($inputs: BulkDeleteSectionsInputs!) {
    bulkDeleteSections(inputs: $inputs) {
      code
      success
      message
      affectedRows
    }
  }
`;
export type BulkDeleteSectionsMutationFn = Apollo.MutationFunction<
  BulkDeleteSectionsMutation,
  BulkDeleteSectionsMutationVariables
>;

/**
 * __useBulkDeleteSectionsMutation__
 *
 * To run a mutation, you first call `useBulkDeleteSectionsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useBulkDeleteSectionsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [bulkDeleteSectionsMutation, { data, loading, error }] = useBulkDeleteSectionsMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useBulkDeleteSectionsMutation(
  baseOptions?: Apollo.MutationHookOptions<
    BulkDeleteSectionsMutation,
    BulkDeleteSectionsMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    BulkDeleteSectionsMutation,
    BulkDeleteSectionsMutationVariables
  >(BulkDeleteSectionsDocument, options);
}
export type BulkDeleteSectionsMutationHookResult = ReturnType<
  typeof useBulkDeleteSectionsMutation
>;
export type BulkDeleteSectionsMutationResult =
  Apollo.MutationResult<BulkDeleteSectionsMutation>;
export type BulkDeleteSectionsMutationOptions = Apollo.BaseMutationOptions<
  BulkDeleteSectionsMutation,
  BulkDeleteSectionsMutationVariables
>;
export const UpdateSectionNameDocument = gql`
  mutation UpdateSectionName($inputs: UpdateSectionNameInputs!) {
    updateSectionName(inputs: $inputs) {
      code
      success
      message
      section {
        id
        name
        updatedAt
      }
    }
  }
`;
export type UpdateSectionNameMutationFn = Apollo.MutationFunction<
  UpdateSectionNameMutation,
  UpdateSectionNameMutationVariables
>;

/**
 * __useUpdateSectionNameMutation__
 *
 * To run a mutation, you first call `useUpdateSectionNameMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateSectionNameMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateSectionNameMutation, { data, loading, error }] = useUpdateSectionNameMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useUpdateSectionNameMutation(
  baseOptions?: Apollo.MutationHookOptions<
    UpdateSectionNameMutation,
    UpdateSectionNameMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    UpdateSectionNameMutation,
    UpdateSectionNameMutationVariables
  >(UpdateSectionNameDocument, options);
}
export type UpdateSectionNameMutationHookResult = ReturnType<
  typeof useUpdateSectionNameMutation
>;
export type UpdateSectionNameMutationResult =
  Apollo.MutationResult<UpdateSectionNameMutation>;
export type UpdateSectionNameMutationOptions = Apollo.BaseMutationOptions<
  UpdateSectionNameMutation,
  UpdateSectionNameMutationVariables
>;
export const UpdateSectionEditorDocument = gql`
  mutation UpdateSectionEditor($inputs: UpdateSectionInputs!) {
    updateSection(inputs: $inputs) {
      code
      success
      message
      section {
        id
        editor
        updatedAt
      }
    }
  }
`;
export type UpdateSectionEditorMutationFn = Apollo.MutationFunction<
  UpdateSectionEditorMutation,
  UpdateSectionEditorMutationVariables
>;

/**
 * __useUpdateSectionEditorMutation__
 *
 * To run a mutation, you first call `useUpdateSectionEditorMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateSectionEditorMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateSectionEditorMutation, { data, loading, error }] = useUpdateSectionEditorMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useUpdateSectionEditorMutation(
  baseOptions?: Apollo.MutationHookOptions<
    UpdateSectionEditorMutation,
    UpdateSectionEditorMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    UpdateSectionEditorMutation,
    UpdateSectionEditorMutationVariables
  >(UpdateSectionEditorDocument, options);
}
export type UpdateSectionEditorMutationHookResult = ReturnType<
  typeof useUpdateSectionEditorMutation
>;
export type UpdateSectionEditorMutationResult =
  Apollo.MutationResult<UpdateSectionEditorMutation>;
export type UpdateSectionEditorMutationOptions = Apollo.BaseMutationOptions<
  UpdateSectionEditorMutation,
  UpdateSectionEditorMutationVariables
>;
export const BulkUpdateSectionEditorsDocument = gql`
  mutation BulkUpdateSectionEditors($inputs: BulkUpdateSectionsInputs!) {
    bulkUpdateSections(inputs: $inputs) {
      code
      success
      message
      sections {
        id
        editor
        updatedAt
      }
    }
  }
`;
export type BulkUpdateSectionEditorsMutationFn = Apollo.MutationFunction<
  BulkUpdateSectionEditorsMutation,
  BulkUpdateSectionEditorsMutationVariables
>;

/**
 * __useBulkUpdateSectionEditorsMutation__
 *
 * To run a mutation, you first call `useBulkUpdateSectionEditorsMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useBulkUpdateSectionEditorsMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [bulkUpdateSectionEditorsMutation, { data, loading, error }] = useBulkUpdateSectionEditorsMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useBulkUpdateSectionEditorsMutation(
  baseOptions?: Apollo.MutationHookOptions<
    BulkUpdateSectionEditorsMutation,
    BulkUpdateSectionEditorsMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    BulkUpdateSectionEditorsMutation,
    BulkUpdateSectionEditorsMutationVariables
  >(BulkUpdateSectionEditorsDocument, options);
}
export type BulkUpdateSectionEditorsMutationHookResult = ReturnType<
  typeof useBulkUpdateSectionEditorsMutation
>;
export type BulkUpdateSectionEditorsMutationResult =
  Apollo.MutationResult<BulkUpdateSectionEditorsMutation>;
export type BulkUpdateSectionEditorsMutationOptions = Apollo.BaseMutationOptions<
  BulkUpdateSectionEditorsMutation,
  BulkUpdateSectionEditorsMutationVariables
>;
export const StartBulkGenerateTakeRunDocument = gql`
  mutation StartBulkGenerateTakeRun($inputs: StartBulkGenerateTakeRunInputs!) {
    startBulkGenerateTakeRun(inputs: $inputs) {
      code
      success
      message
      runId
    }
  }
`;
export type StartBulkGenerateTakeRunMutationFn = Apollo.MutationFunction<
  StartBulkGenerateTakeRunMutation,
  StartBulkGenerateTakeRunMutationVariables
>;

/**
 * __useStartBulkGenerateTakeRunMutation__
 *
 * To run a mutation, you first call `useStartBulkGenerateTakeRunMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useStartBulkGenerateTakeRunMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [startBulkGenerateTakeRunMutation, { data, loading, error }] = useStartBulkGenerateTakeRunMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useStartBulkGenerateTakeRunMutation(
  baseOptions?: Apollo.MutationHookOptions<
    StartBulkGenerateTakeRunMutation,
    StartBulkGenerateTakeRunMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    StartBulkGenerateTakeRunMutation,
    StartBulkGenerateTakeRunMutationVariables
  >(StartBulkGenerateTakeRunDocument, options);
}
export type StartBulkGenerateTakeRunMutationHookResult = ReturnType<
  typeof useStartBulkGenerateTakeRunMutation
>;
export type StartBulkGenerateTakeRunMutationResult =
  Apollo.MutationResult<StartBulkGenerateTakeRunMutation>;
export type StartBulkGenerateTakeRunMutationOptions = Apollo.BaseMutationOptions<
  StartBulkGenerateTakeRunMutation,
  StartBulkGenerateTakeRunMutationVariables
>;
export const ActiveBulkGenerateTakeRunDocument = gql`
  query ActiveBulkGenerateTakeRun($inputs: ActiveBulkGenerateTakeRunInputs!) {
    activeBulkGenerateTakeRun(inputs: $inputs) {
      runId
      pendingItems {
        sectionId
        text
        formattedText
        avatarId
        avatarVariantId
        ttsVersion
      }
    }
  }
`;

/**
 * __useActiveBulkGenerateTakeRunQuery__
 *
 * To run a query within a React component, call `useActiveBulkGenerateTakeRunQuery` and pass it any options that fit your needs.
 * When your component renders, `useActiveBulkGenerateTakeRunQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useActiveBulkGenerateTakeRunQuery({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useActiveBulkGenerateTakeRunQuery(
  baseOptions: Apollo.QueryHookOptions<
    ActiveBulkGenerateTakeRunQuery,
    ActiveBulkGenerateTakeRunQueryVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<
    ActiveBulkGenerateTakeRunQuery,
    ActiveBulkGenerateTakeRunQueryVariables
  >(ActiveBulkGenerateTakeRunDocument, options);
}
export function useActiveBulkGenerateTakeRunLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<
    ActiveBulkGenerateTakeRunQuery,
    ActiveBulkGenerateTakeRunQueryVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    ActiveBulkGenerateTakeRunQuery,
    ActiveBulkGenerateTakeRunQueryVariables
  >(ActiveBulkGenerateTakeRunDocument, options);
}
export type ActiveBulkGenerateTakeRunQueryHookResult = ReturnType<
  typeof useActiveBulkGenerateTakeRunQuery
>;
export type ActiveBulkGenerateTakeRunLazyQueryHookResult = ReturnType<
  typeof useActiveBulkGenerateTakeRunLazyQuery
>;
export type ActiveBulkGenerateTakeRunQueryResult = Apollo.QueryResult<
  ActiveBulkGenerateTakeRunQuery,
  ActiveBulkGenerateTakeRunQueryVariables
>;
export const CancelBulkGenerateTakeRunDocument = gql`
  mutation CancelBulkGenerateTakeRun($inputs: CancelBulkGenerateTakeRunInputs!) {
    cancelBulkGenerateTakeRun(inputs: $inputs) {
      code
      success
      message
      canceled
    }
  }
`;
export type CancelBulkGenerateTakeRunMutationFn = Apollo.MutationFunction<
  CancelBulkGenerateTakeRunMutation,
  CancelBulkGenerateTakeRunMutationVariables
>;

/**
 * __useCancelBulkGenerateTakeRunMutation__
 *
 * To run a mutation, you first call `useCancelBulkGenerateTakeRunMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCancelBulkGenerateTakeRunMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [cancelBulkGenerateTakeRunMutation, { data, loading, error }] = useCancelBulkGenerateTakeRunMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useCancelBulkGenerateTakeRunMutation(
  baseOptions?: Apollo.MutationHookOptions<
    CancelBulkGenerateTakeRunMutation,
    CancelBulkGenerateTakeRunMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    CancelBulkGenerateTakeRunMutation,
    CancelBulkGenerateTakeRunMutationVariables
  >(CancelBulkGenerateTakeRunDocument, options);
}
export type CancelBulkGenerateTakeRunMutationHookResult = ReturnType<
  typeof useCancelBulkGenerateTakeRunMutation
>;
export type CancelBulkGenerateTakeRunMutationResult =
  Apollo.MutationResult<CancelBulkGenerateTakeRunMutation>;
export type CancelBulkGenerateTakeRunMutationOptions = Apollo.BaseMutationOptions<
  CancelBulkGenerateTakeRunMutation,
  CancelBulkGenerateTakeRunMutationVariables
>;
export const BulkRunItemChangedDocument = gql`
  subscription BulkRunItemChanged($inputs: BulkRunItemChangedSubscriptionInputs!) {
    bulkRunItemChanged(inputs: $inputs) {
      projectId
      runId
      bulkRunItemId
      sectionId
      status
      takeId
      errorCode
      errorMessage
    }
  }
`;

/**
 * __useBulkRunItemChangedSubscription__
 *
 * To run a query within a React component, call `useBulkRunItemChangedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useBulkRunItemChangedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useBulkRunItemChangedSubscription({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useBulkRunItemChangedSubscription(
  baseOptions: Apollo.SubscriptionHookOptions<
    BulkRunItemChangedSubscription,
    BulkRunItemChangedSubscriptionVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useSubscription<
    BulkRunItemChangedSubscription,
    BulkRunItemChangedSubscriptionVariables
  >(BulkRunItemChangedDocument, options);
}
export type BulkRunItemChangedSubscriptionHookResult = ReturnType<
  typeof useBulkRunItemChangedSubscription
>;
export type BulkRunItemChangedSubscriptionResult =
  Apollo.SubscriptionResult<BulkRunItemChangedSubscription>;
export const BulkRunStartedDocument = gql`
  subscription BulkRunStarted($inputs: BulkRunStartedSubscriptionInputs!) {
    bulkRunStarted(inputs: $inputs) {
      projectId
      runId
    }
  }
`;

/**
 * __useBulkRunStartedSubscription__
 *
 * To run a query within a React component, call `useBulkRunStartedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useBulkRunStartedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useBulkRunStartedSubscription({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useBulkRunStartedSubscription(
  baseOptions: Apollo.SubscriptionHookOptions<
    BulkRunStartedSubscription,
    BulkRunStartedSubscriptionVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useSubscription<
    BulkRunStartedSubscription,
    BulkRunStartedSubscriptionVariables
  >(BulkRunStartedDocument, options);
}
export type BulkRunStartedSubscriptionHookResult = ReturnType<
  typeof useBulkRunStartedSubscription
>;
export type BulkRunStartedSubscriptionResult =
  Apollo.SubscriptionResult<BulkRunStartedSubscription>;
export const UpdateSectionVoiceDocument = gql`
  mutation UpdateSectionVoice($inputs: UpdateSectionVoiceInputs!) {
    updateSectionVoice(inputs: $inputs) {
      code
      success
      message
      section {
        id
        defaultAvatarId
        defaultAvatarVariantId
        defaultTtsVersion
        projectId
      }
      project {
        id
        voices {
          id
        }
      }
    }
  }
`;
export type UpdateSectionVoiceMutationFn = Apollo.MutationFunction<
  UpdateSectionVoiceMutation,
  UpdateSectionVoiceMutationVariables
>;

/**
 * __useUpdateSectionVoiceMutation__
 *
 * To run a mutation, you first call `useUpdateSectionVoiceMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateSectionVoiceMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateSectionVoiceMutation, { data, loading, error }] = useUpdateSectionVoiceMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useUpdateSectionVoiceMutation(
  baseOptions?: Apollo.MutationHookOptions<
    UpdateSectionVoiceMutation,
    UpdateSectionVoiceMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    UpdateSectionVoiceMutation,
    UpdateSectionVoiceMutationVariables
  >(UpdateSectionVoiceDocument, options);
}
export type UpdateSectionVoiceMutationHookResult = ReturnType<
  typeof useUpdateSectionVoiceMutation
>;
export type UpdateSectionVoiceMutationResult =
  Apollo.MutationResult<UpdateSectionVoiceMutation>;
export type UpdateSectionVoiceMutationOptions = Apollo.BaseMutationOptions<
  UpdateSectionVoiceMutation,
  UpdateSectionVoiceMutationVariables
>;
export const UpdateSectionVoicesDocument = gql`
  mutation UpdateSectionVoices($inputs: UpdateSectionVoicesInputs!) {
    updateSectionVoices(inputs: $inputs) {
      code
      success
      message
      sections {
        id
        defaultAvatarId
        defaultAvatarVariantId
        defaultTtsVersion
        projectId
      }
      projects {
        id
        voices {
          id
        }
      }
    }
  }
`;
export type UpdateSectionVoicesMutationFn = Apollo.MutationFunction<
  UpdateSectionVoicesMutation,
  UpdateSectionVoicesMutationVariables
>;

/**
 * __useUpdateSectionVoicesMutation__
 *
 * To run a mutation, you first call `useUpdateSectionVoicesMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateSectionVoicesMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateSectionVoicesMutation, { data, loading, error }] = useUpdateSectionVoicesMutation({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useUpdateSectionVoicesMutation(
  baseOptions?: Apollo.MutationHookOptions<
    UpdateSectionVoicesMutation,
    UpdateSectionVoicesMutationVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    UpdateSectionVoicesMutation,
    UpdateSectionVoicesMutationVariables
  >(UpdateSectionVoicesDocument, options);
}
export type UpdateSectionVoicesMutationHookResult = ReturnType<
  typeof useUpdateSectionVoicesMutation
>;
export type UpdateSectionVoicesMutationResult =
  Apollo.MutationResult<UpdateSectionVoicesMutation>;
export type UpdateSectionVoicesMutationOptions = Apollo.BaseMutationOptions<
  UpdateSectionVoicesMutation,
  UpdateSectionVoicesMutationVariables
>;
export const SectionsChangedDocument = gql`
  subscription SectionsChanged($inputs: SectionsChangedSubscriptionInputs!) {
    sectionsChanged(inputs: $inputs) {
      projectId
      kind
      sectionIds
      sections {
        ...SectionFragment
      }
    }
  }
  ${SectionFragmentDoc}
`;

/**
 * __useSectionsChangedSubscription__
 *
 * To run a query within a React component, call `useSectionsChangedSubscription` and pass it any options that fit your needs.
 * When your component renders, `useSectionsChangedSubscription` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the subscription, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useSectionsChangedSubscription({
 *   variables: {
 *      inputs: // value for 'inputs'
 *   },
 * });
 */
export function useSectionsChangedSubscription(
  baseOptions: Apollo.SubscriptionHookOptions<
    SectionsChangedSubscription,
    SectionsChangedSubscriptionVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useSubscription<
    SectionsChangedSubscription,
    SectionsChangedSubscriptionVariables
  >(SectionsChangedDocument, options);
}
export type SectionsChangedSubscriptionHookResult = ReturnType<
  typeof useSectionsChangedSubscription
>;
export type SectionsChangedSubscriptionResult =
  Apollo.SubscriptionResult<SectionsChangedSubscription>;
